import { DateTime } from "luxon";
import { BookingScheduleInterface } from "../../../../../features/Booking-Form/typings/booking.types";
import { UserInformation } from "../../../../../features/Login/typings/login.types";
import { Colleague } from "../../../../../features/Connections/types/Colleague.type";
import { BookingType } from "../../../../../features/Booking-Form/typings/booking-inputs";
import { NoPlaceZoneBooking } from "../../../../../features/Booking-Form/typings/booking-service-administration.types";

// Helper function to check if schedule is of type NoPlaceZoneBooking
function isNoPlaceZoneBooking(schedule: any): schedule is NoPlaceZoneBooking {
  return (schedule as NoPlaceZoneBooking)?.serviceBookings !== undefined;
}

/** Prefer connection colleague companyId, then schedule-colleague directory map. */
export function resolveBookedForCompanyId({
  userBookedFor,
  remoteColleagues,
  companyIdByUserId
}: {
  userBookedFor: string;
  remoteColleagues: Colleague[] | undefined;
  companyIdByUserId?: Readonly<Record<string, number>>;
}): number | undefined {
  const fromRemote = remoteColleagues?.find(col => col.userId === userBookedFor)?.companyId;
  if (fromRemote != null) {
    return fromRemote;
  }
  return companyIdByUserId?.[userBookedFor];
}

/** Builds a userId → companyId map from schedule-colleague / directory rows. */
export function buildCompanyIdByUserId(
  users: ReadonlyArray<{ userId?: string; companyId?: number }> | undefined
): Record<string, number> {
  const map: Record<string, number> = {};
  for (const user of users ?? []) {
    if (user.userId && user.companyId != null) {
      map[user.userId] = user.companyId;
    }
  }
  return map;
}

export function generateScheduleType({
  schedule,
  userInformation,
  remoteColleagues,
  isCompanyAdmin = false,
  companyIdByUserId
}: {
  schedule: BookingScheduleInterface;
  userInformation: UserInformation;
  remoteColleagues: Colleague[] | undefined;
  /** From `state.login.userRoles`, not `UserInformation`. */
  isCompanyAdmin?: boolean;
  /** Schedule-colleague directory (includes company-admin company users). */
  companyIdByUserId?: Readonly<Record<string, number>>;
}) {
  const isUpcomingSchedule =
    DateTime.fromISO(schedule.startDate).valueOf() > DateTime.now().valueOf();

  /** user's schedule is deletable */
  const isUserScheduleDeletable =
    isUpcomingSchedule && schedule.userBookedFor === userInformation.sub && !schedule.isGuest;

  /** schedule user's booking connect status */
  const isScheduleColleaguesCancelAllow =
    remoteColleagues?.find(col => col.userId === schedule.userBookedFor)?.teamBookingColleague ?? // previously teamBookingPermission
    false;

  const isOtherUserBooking = schedule.userBookedFor !== userInformation.sub;
  const isUpcomingOtherUserBooking = isUpcomingSchedule && isOtherUserBooking;

  const isColleagueViaTeamBooking =
    userInformation.company.meta.allowBookingCancellations && isScheduleColleaguesCancelAllow;

  const bookedForCompanyId = resolveBookedForCompanyId({
    userBookedFor: schedule.userBookedFor,
    remoteColleagues,
    companyIdByUserId
  });

  const isCompanyAdminSameCompany =
    isCompanyAdmin &&
    bookedForCompanyId != null &&
    bookedForCompanyId === userInformation.companyId;

  /** Upcoming other-user + team-booking setting (not company-admin calendar edit). */
  const isUpcomingTeamBookingColleagueEditable =
    isUpcomingOtherUserBooking && isColleagueViaTeamBooking;

  /**
   * Calendar dialog: colleague setting path stays upcoming-only;
   * same-company company admin may edit running and past (#1577).
   */
  const isColleagueScheduleEditable =
    isUpcomingTeamBookingColleagueEditable ||
    (isOtherUserBooking && !schedule.isGuest && isCompanyAdminSameCompany);

  /**
   * Check-in / unlock chrome: own booking or upcoming team-booking colleague.
   * Company-admin past/running edit (#1577) must not show these — BE stays
   * owner/colleague via `checkBookingBelongsToRequestingUser`.
   */
  const isColleagueCheckInUnlockAllowed = isUpcomingTeamBookingColleagueEditable;

  /**
   * Bulk/table selection follows team-booking setting only — not company-admin bypass.
   * Spec: bulk-select/delete follows `allowBookingCancellations`.
   */
  const isBulkDeleteSelectable = isUserScheduleDeletable || isUpcomingTeamBookingColleagueEditable;

  const checkBookingTypeZone =
    schedule.bookingType === BookingType.PLACEZONE ||
    schedule.bookingType === BookingType.CONFERENCEZONE;
  /** show conference booking service when the value exists */
  const isConferenceZoneBooking =
    schedule.bookingType === BookingType.CONFERENCEZONE &&
    userInformation.company.meta.activateBookingServices &&
    isNoPlaceZoneBooking(schedule); // Check if schedule is of type NoPlaceZoneBooking

  return {
    isUserScheduleDeletable,
    isColleagueScheduleEditable,
    isColleagueCheckInUnlockAllowed,
    isBulkDeleteSelectable,
    checkBookingTypeZone,
    isConferenceZoneBooking
  };
}
